import {
  type ReceivingSession,
  ReceivingSessionSchema,
  type VendorInstructions,
  VendorInstructionsSchema,
} from '../data/receiving';
import type { FetchWrapper } from '../helpers/fetch-wrapper';
import {
  createMultipleHandler,
  createSingleHandler,
  handleVoidResponse,
} from '../helpers/fetch-wrapper';
import { storesBaseUrl as baseUrl } from './base';

export type ReceivingIntake = 'paste' | 'file';

export interface LeaseResult {
  granted: boolean;
  current_holder?: string;
}

export interface PresignedUploadTarget {
  url: string;
  s3_key: string;
  fields?: Record<string, string>;
  // Headers the PUT must carry for the presigned signature to verify. The AWS
  // signer decides which values live in the URL's query string and which have
  // to be sent as headers, and that split has changed between SDK patch
  // releases — so send back whatever the server hands over rather than
  // encoding either shape here. Optional: an older API omits it.
  headers?: Record<string, string>;
}

export class ReceivingService {
  constructor(
    private readonly fetch: FetchWrapper,
    private readonly tabId: string
  ) {}

  getVendorInstructions(
    storeId: string,
    vendorNormalizedName: string
  ): Promise<VendorInstructions> {
    return this.fetch
      .get(`${baseUrl}/${storeId}/receiving/instructions/${vendorNormalizedName}`)
      .then(createSingleHandler(VendorInstructionsSchema));
  }

  listActiveSessions(storeId: string): Promise<ReceivingSession[]> {
    return this.fetch
      .get(`${baseUrl}/${storeId}/receiving-sessions`)
      .then(createMultipleHandler(ReceivingSessionSchema));
  }

  getSession(storeId: string, sessionId: string): Promise<ReceivingSession> {
    return this.fetch
      .get(`${baseUrl}/${storeId}/receiving-sessions/${sessionId}`)
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  startSession(storeId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(`${baseUrl}/${storeId}/receiving-sessions`, {}, { headers: { 'X-Tab-ID': this.tabId } })
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  setCompanion(storeId: string, sessionId: string, machineId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/companion`,
        { machine_id: machineId },
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  claimSession(storeId: string, sessionId: string): Promise<LeaseResult> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/claim`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(async (res) => {
        // 409 (lease denied) is a normal answer here, not a failure — it still
        // carries a valid LeaseResult naming the current holder.
        if (res.status !== 409) {
          await handleVoidResponse(res, 'claim session');
        }

        return (await res.json()) as LeaseResult;
      });
  }

  setVendor(storeId: string, sessionId: string, vendorIds: string[]): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/set-vendor`,
        { vendor_ids: vendorIds },
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  async uploadFile(storeId: string, sessionId: string, file: File): Promise<void> {
    const sha256 = await this.computeSHA256(file);
    const target = await this.getPresignedUpload(storeId, sessionId, file, sha256);
    await this.putToS3(target, file);
  }

  processInvoices(
    storeId: string,
    sessionId: string,
    intake: ReceivingIntake
  ): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/process?intake=${intake}`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  /**
   * Confirm the current review results and trigger LPOS file write.
   * No payload is sent — the backend reads the LPOS blob from the stored
   * ProcessingResult.ReceivingFile.Content (Phase A design decision in !3339).
   * `receiveDuplicates` is the ORD-451 override: the backend refuses with a
   * 409 while the session has flagged duplicates unless it is set.
   */
  confirm(
    storeId: string,
    sessionId: string,
    { receiveDuplicates = false }: { receiveDuplicates?: boolean } = {}
  ): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/confirm`,
        { receive_duplicates: receiveDuplicates },
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  retry(storeId: string, sessionId: string): Promise<ReceivingSession> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/retry`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then(createSingleHandler(ReceivingSessionSchema));
  }

  cancel(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/cancel`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => handleVoidResponse(res, 'cancel'));
  }

  // POST /resume — triggers the backend to push session_resume_probe to the
  // companion. 409 when the companion is offline (the probe is not replayed on
  // reconnect, so the user must retry once it's back).
  resume(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/resume`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => handleVoidResponse(res, 'resume'));
  }

  // POST /abandon — initiates the clear_rcv_file handshake; backend resolves
  // on rcv_clear_result and transitions the session or refuses (busy:true).
  // 409 when the companion is offline (the clear can't be delivered).
  abandon(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/abandon`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => {
        // 404 = the session is already gone, which is exactly what Abandon
        // aims for — a benign lost-race, not a failure. Resolve so the caller
        // doesn't log an error or show the user an unactionable message. The
        // lease wrapper is the only source of a 404 here, so it can only mean
        // session-not-found; cancel/resume stay strict.
        if (res.status === 404) {
          return;
        }

        return handleVoidResponse(res, 'abandon');
      });
  }

  requestFileStatusCheck(storeId: string, sessionId: string): Promise<void> {
    return this.fetch
      .post(
        `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/check-file-status`,
        {},
        { headers: { 'X-Tab-ID': this.tabId } }
      )
      .then((res) => handleVoidResponse(res, 'file-status check'));
  }

  private async computeSHA256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);

    return btoa(String.fromCharCode(...new Uint8Array(digest)));
  }

  private async getPresignedUpload(
    storeId: string,
    sessionId: string,
    file: File,
    sha256: string
  ): Promise<PresignedUploadTarget> {
    const res = await this.fetch.post(
      `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/upload-url`,
      { filename: file.name, content_type: file.type, sha256, size: file.size },
      { headers: { 'X-Tab-ID': this.tabId } }
    );
    await handleVoidResponse(res, 'upload preparation');

    return (await res.json()) as PresignedUploadTarget;
  }

  private async putToS3(target: PresignedUploadTarget, file: File): Promise<void> {
    const res = await fetch(target.url, {
      method: 'PUT',
      // target.headers is the signer's own header set — echo it verbatim, do
      // not filter or add to it, or the signature stops verifying.
      headers: { 'Content-Type': file.type, ...(target.headers ?? {}) },
      body: file,
    });
    if (!res.ok) {
      // S3 names the reason in an XML body. Without it every rejection reads
      // the same, and "403" alone cannot tell a missing signed header
      // (SignatureDoesNotMatch) from an expired URL.
      const body = await res.text().catch(() => '');
      const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1] ?? res.statusText;

      throw new Error(`S3 upload failed: ${res.status} ${code}`);
    }
  }
}
